import { Suspense } from 'react';
import { element, bool } from 'prop-types';
import { QueryClientProvider, QueryClient } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

const normalOptions = {
	defaultOptions: {
		queries: {
			suspense: true,
		},
	},
};

const unitTestOptions = {
	defaultOptions: {
		queries: {
			// TODO: Research why suspense sometimes makes certain tests brittle on some Windows computers but not Macs.
			// Since the front end team and everyone working on this project uses a Mac, we're going to keep it turned
			// on. If it becomes an issue, we'll either turn it back off or try to find a way to fix it. It's just difficult
			// to research right now because suspense is a new feature so there isn't a lot of info on it online yet.
			suspense: true,
			retry: false,
		},
	},
};

const DataFetchingWrapper = ({
	SuspenseFallBack,
	hasOwnProvider,
	children,
}) => {
	const queryClientOptions =
		process.env.NODE_ENV === 'test' ? unitTestOptions : normalOptions;
	const queryClient = new QueryClient(queryClientOptions);

	const getSuspenseWrappedComponent = () => (
		<Suspense fallback={SuspenseFallBack}>{children}</Suspense>
	);

	return hasOwnProvider ? (
		getSuspenseWrappedComponent()
	) : (
		// Only add the QueryClientProvider if the consuming app doesn't have it. (Having 2 providers can cause redundant API calls.)
		<QueryClientProvider client={queryClient}>
			{getSuspenseWrappedComponent()}
			{/*  We only want the devtools (the red flower on the bottom left) to show in dev mode */}
			{process.env.NODE_ENV === 'development' && (
				<ReactQueryDevtools initialIsOpen={false} />
			)}
		</QueryClientProvider>
	);
};

DataFetchingWrapper.propTypes = {
	/**
	 * The component to show when the data is loading.
	 */
	SuspenseFallBack: element.isRequired,
	/**
	 * If the consuming app is using react-query and has its own QueryClientProvider tags, this should be set to
	 * true. Any consuming apps not using react-query should have this set to false or just leave it undefined.
	 */
	hasOwnProvider: bool,
	/**
	 * The component we want to enable data fetching on.
	 */
	children: element.isRequired,
};

export default DataFetchingWrapper;
